<template>
 <div class="ip-stats">
 <el-row :gutter="20" class="card-row">
 <el-col :span="24">
 <div class="chart-card">
 <div class="chart-header">
 <el-date-picker
 v-model="dateRange"
 type="daterange"
 range-separator="至"
 start-placeholder="开始日期"
 end-placeholder="结束日期"
 value-format="yyyy-MM-dd"
 :picker-options="pickerOptions"
 @change="loadAll"
 />
 <el-button type="primary" size="small" @click="loadAll" style="margin-left:12px">刷新</el-button>
 </div>
 <div ref="locationChart" class="chart-body"></div>
 </div>
 </el-col>
 </el-row>

 <el-row :gutter="20" class="card-row">
 <el-col :span="24">
 <div class="chart-card">
 <div class="chart-header">
 <el-pagination
 @current-change="handlePageChange"
 :current-page="page"
 :page-size="pageSize"
 :total="total"
 layout="total, prev, pager, next"
 style="float:right"
 />
 </div>
 <div class="filter-bar-inner">
 <el-input v-model="filters.ip_keyword" placeholder="搜索IP" size="small" clearable style="width:160px" @keyup.enter.native="searchData" @clear="searchData" />
 <el-input v-model="filters.location_keyword" placeholder="搜索归属地" size="small" clearable style="width:160px" @keyup.enter.native="searchData" @clear="searchData" />
 <el-select v-model="filters.browser_filter" placeholder="浏览器" size="small" clearable style="width:130px">
 <el-option v-for="b in filterOptions.browsers" :key="b" :label="b" :value="b" />
 </el-select>
 <el-select v-model="filters.os_filter" placeholder="操作系统" size="small" clearable style="width:130px">
 <el-option v-for="o in filterOptions.oses" :key="o" :label="o" :value="o" />
 </el-select>
 <el-select v-model="filters.device_filter" placeholder="设备" size="small" clearable style="width:110px">
 <el-option v-for="d in filterOptions.devices" :key="d" :label="d" :value="d" />
 </el-select>
 <el-button type="primary" size="small" @click="searchData">筛选</el-button>
 <el-button size="small" @click="resetFilters">重置</el-button>
 </div>
 <el-table :data="ipList" stripe size="small" style="width:100%" v-loading="loading">
 <el-table-column type="index" label="#" width="60" />
 <el-table-column label="IP地址" width="180">
   <template slot-scope="{ row }">
     <span style="font-family:monospace;font-size:12px">{{ row.ip }}</span>
     <el-tag v-if="blockedIps.has(row.ip)" size="mini" type="danger" effect="dark" style="margin-left:4px">已禁用</el-tag>
   </template>
 </el-table-column>
 <el-table-column prop="ip_location" label="归属地" width="180" />
 <el-table-column prop="browser" label="浏览器" width="110" />
 <el-table-column prop="os" label="操作系统" width="110" />
 <el-table-column prop="device" label="设备" width="90" />
 <el-table-column prop="visit_count" label="访问次数" width="110" sortable />
 <el-table-column prop="last_visit" label="最后访问时间" min-width="180">
 <template slot-scope="{row}">{{ formatTime(row.last_visit) }}</template>
 </el-table-column>
 </el-table>
 </div>
 </el-col>
 </el-row>
 </div>
</template>

<script>
import * as echarts from 'echarts';
import { statsApi } from '../api';
import dayjs from 'dayjs';

export default {
 name: 'IpStats',
 data() {
 return {
 dateRange: [dayjs().subtract(30, 'day').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')],
 ipList: [],
 page: 1,
 pageSize: 20,
 total: 0,
 loading: false,
 blockedIps: new Set(),
 filters: { ip_keyword: '', location_keyword: '', browser_filter: '', os_filter: '', device_filter: '' },
 filterOptions: { browsers: [], oses: [], devices: [] },
 charts: {},
 pickerOptions: {
 shortcuts: [
 { text: '今天', onClick(picker) { const d = dayjs().format('YYYY-MM-DD'); picker.$emit('pick', [d, d]); } },
 { text: '近7天', onClick(picker) { picker.$emit('pick', [dayjs().subtract(6, 'day').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')]); } },
 { text: '近30天', onClick(picker) { picker.$emit('pick', [dayjs().subtract(29, 'day').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')]); } },
 { text: '本月', onClick(picker) { picker.$emit('pick', [dayjs().startOf('month').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')]); } },
 ]
 }
 };
 },
 mounted() {
 this.loadBlockedIps();
 this.loadAll();
 window.addEventListener('resize', this.resizeCharts);
 },
 beforeDestroy() {
 window.removeEventListener('resize', this.resizeCharts);
 Object.values(this.charts).forEach(c => c && c.dispose());
 },
 methods: {
 async loadBlockedIps() {
 try {
 const res = await statsApi.getBlockedIps();
 if (res.code === 0) this.blockedIps = new Set(res.data || []);
 } catch (e) { console.error(e); }
 },
 loadAll() {
 const [startDate, endDate] = this.dateRange || [];
 const params = { start_date: startDate, end_date: endDate };
 this.loadLocationStats(params);
 this.loadFilterOptions(params);
 this.page = 1;
 this.loadIp(params);
 },
 async loadIp(params) {
 this.loading = true;
 try {
 const res = await statsApi.ip({ ...params, ...this.filters, page: this.page, page_size: this.pageSize });
 if (res.code === 0) { this.ipList = res.data.list; this.total = res.data.total; }
 } catch (e) { console.error(e); }
 finally { this.loading = false; }
 },
 async loadLocationStats(params) {
 try {
 const res = await statsApi.locationStats({ ...params, limit: 20 });
 if (res.code !== 0) return;
 const data = res.data;
 this.renderChart('locationChart', {
 tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, backgroundColor: 'rgba(30,41,59,0.92)', borderColor: 'rgba(91,141,239,0.15)', textStyle: { color: '#fff', fontSize: 12 } },
 grid: { left: 60, right: 25, top: 25, bottom: 55 },
 xAxis: { type: 'category', data: data.map(d => d.location), axisLabel: { rotate: 35, fontSize: 11, color: '#64748b', interval: 0 }, axisLine: { lineStyle: { color: '#cbd5e1' } } },
 yAxis: { type: 'value', axisLabel: { color: '#64748b' }, splitLine: { lineStyle: { color: '#f1f3f5' } } },
 dataZoom: [{ type: 'inside' }, { type: 'slider', height: 20, bottom: 10, borderColor: '#e2e8f0', fillerColor: 'rgba(91,141,239,0.06)' }],
 series: [{ name: '访问次数', type: 'bar', data: data.map(d => +d.visit_count), itemStyle: { color: new echarts.graphic.LinearGradient(0,0,0,1,[{offset:0,color:'#5b8def'},{offset:1,color:'rgba(91,141,239,0.15)'}]), borderRadius: [4,4,0,0] }, label: { show: true, position: 'top', fontSize: 11, color: '#64748b', formatter: '{c}' } }]
 });
 } catch (e) { console.error(e); }
 },
 async loadFilterOptions(params) {
 try {
 const res = await statsApi.device(params);
 if (res.code !== 0) return;
 this.filterOptions.browsers = res.data.browsers.map(b => b.browser).filter(Boolean);
 this.filterOptions.oses = res.data.oses.map(o => o.os).filter(Boolean);
 this.filterOptions.devices = res.data.devices.map(d => d.device).filter(Boolean);
 } catch (e) { console.error(e); }
 },
 searchData() {
 this.page = 1;
 const [startDate, endDate] = this.dateRange || [];
 this.loadIp({ start_date: startDate, end_date: endDate });
 },
 resetFilters() {
 this.filters = { ip_keyword: '', location_keyword: '', browser_filter: '', os_filter: '', device_filter: '' };
 this.page = 1;
 const [startDate, endDate] = this.dateRange || [];
 this.loadIp({ start_date: startDate, end_date: endDate });
 },
 handlePageChange(p) {
 this.page = p;
 const [startDate, endDate] = this.dateRange || [];
 this.loadIp({ start_date: startDate, end_date: endDate });
 },
 renderChart(ref, option) {
 this.$nextTick(() => {
 const el = this.$refs[ref];
 if (!el) return;
 if (this.charts[ref]) this.charts[ref].dispose();
 this.charts[ref] = echarts.init(el);
 this.charts[ref].setOption(option);
 });
 },
 resizeCharts() {
 Object.values(this.charts).forEach(c => c && c.resize());
 },
 formatTime(t) {
 return t ? dayjs(t).format('YYYY-MM-DD HH:mm:ss') : '-';
 }
 }
};
</script>

<style scoped>
.filter-bar { display: flex; align-items: center; margin-bottom: 18px; }
.card-row { margin-bottom: 18px; }

.chart-card { background: #fff; border-radius: 8px; box-shadow: 0 1px 3px rgba(0,0,0,0.04); border: 1px solid #eef0f3; overflow: hidden; }
.chart-header { padding: 14px 20px; border-bottom: 1px solid #f0f1f3; display: flex; align-items: center; justify-content: space-between; }
.chart-title { font-size: 14px; font-weight: 600; color: #1e293b; }
.chart-body { height: 380px; padding: 12px; }

.filter-bar-inner { display: flex; align-items: center; gap: 8px; padding: 12px 16px; border-bottom: 1px solid #f0f1f3; flex-wrap: wrap; background: #fafbfc; }
</style>